<template>
  <div class="sa-voice-hold-wrap">
    <button
      ref="button"
      type="button"
      class="sa-voice-hold"
      :class="{ 'sa-voice-hold-down': down, 'sa-voice-hold-idle': !live }"
      :aria-pressed="down ? 'true' : 'false'"
      :aria-label="label"
      @pointerdown="onPointerDown"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @lostpointercapture="onPointerUp"
      @contextmenu.prevent
      @click="onClick"
    >
      <!-- Same glyph as VoiceControl.vue's launcher, so the thing you press to
           open voice and the thing you press to speak are recognisably one
           feature. -->
      <svg
        viewBox="0 0 24 24"
        width="34"
        height="34"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
      </svg>
    </button>

    <!-- aria-hidden because the button already carries this exact string as its
         accessible name; without it a screen reader reads the instruction
         twice. -->
    <p class="sa-voice-hold-label" aria-hidden="true">{{ label }}</p>
    <p v-if="isHold" class="sa-voice-hold-hint">or hold the space bar</p>
  </div>
</template>

<script>
/**
 * The hold-to-talk button.
 *
 * It owns no session and no audio. It reports two things — the press began, the
 * press ended — and the dock turns those into `session.setGate(true/false)` on
 * ONE hands-free session. See `src/utils/voiceHoldGate.js` for why a hold is a
 * gate rather than a session.
 *
 * `holding` is a prop, not local state, deliberately: the gate the user is
 * actually being recorded through lives on the session, so the button renders
 * what the dock says is true rather than what it hopes happened. The internal
 * `pointerHeld`/`keyHeld` pair exists only to decide when a press has begun and
 * ended, so that holding the space bar AND the button emits one `hold-start`
 * and one `hold-end` rather than two of each.
 *
 * A stuck-open gate is the failure mode worth engineering against — it is a
 * live microphone the user believes is off — so every path that can lose a
 * press releases it: `pointercancel`, `lostpointercapture`, window blur, a mode
 * change, and unmount.
 */

/** Space bar, spelled for `code` and for both historical `key` values. */
function isSpaceKey(event) {
  return event.code === "Space" || event.key === " " || event.key === "Spacebar";
}

/**
 * Somewhere the space bar means "space", not "talk".
 *
 * `isContentEditable` covers the rich-text case; the tag list covers the rest.
 * A button is deliberately NOT in it: this button itself is focusable, and
 * space on it must hold rather than type.
 */
function isTypingTarget(target) {
  if (!target || target.nodeType !== 1) return false;
  if (target.isContentEditable) return true;
  const tag = String(target.tagName || "").toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export default {
  name: "VoiceHoldButton",
  props: {
    // 'hold' — press and hold. 'tap' — tap to talk or finish.
    // 'free' — hands-free, one tap toggles
    // the gate. Anything else is treated as 'hold', which is the mode that
    // cannot leave a microphone open by accident.
    mode: {
      type: String,
      default: "hold",
      validator: (value) => ["hold", "tap", "free"].includes(value),
    },
    // A session is running. Purely presentational here: the button still emits
    // when it is false, so the dock is free to treat the first press as "start
    // a session and open the gate" rather than making the user press twice.
    live: {
      type: Boolean,
      default: false,
    },
    // The gate is currently open, as the dock understands it.
    holding: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["hold-start", "hold-end", "toggle-free", "toggle-hold"],
  data() {
    return {
      pointerHeld: false,
      keyHeld: false,
      capturedPointerId: null,
      tapPointerId: null,
      tapKeyHeld: false,
    };
  },
  computed: {
    isHold() {
      return this.mode !== "free" && this.mode !== "tap";
    },

    down() {
      return !!this.holding;
    },

    // Both pairs name the NEXT action, not the current state, because that is
    // what someone reads a control for.
    label() {
      if (this.mode === "tap") return this.down ? "Tap when done" : "Tap to talk";
      if (this.isHold) return this.down ? "Release to send" : "Hold to talk";
      return this.down ? "Tap to mute" : "Tap to unmute";
    },
  },
  watch: {
    // Switching to hands-free mid-press would otherwise leave the gate open
    // with nothing left that could close it.
    mode() {
      this.releaseAll();
    },
  },
  mounted() {
    // On the document, not the button: the point of the space bar is that the
    // user can be looking at the grid, with focus anywhere harmless, and still
    // talk. Bubble phase rather than capture, so anything that genuinely wants
    // the space bar first can still stop it.
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.releaseAll);
    // A tab switch or a phone app-switch can hide the page without blurring
    // the window; on a phone it is the only reliable signal. A hidden page must
    // not keep the gate open behind a button nobody can see (review A2).
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.releaseAll);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    // Unmounting mid-press must not leave the dock holding an open gate.
    this.releaseAll();
  },
  methods: {
    onVisibilityChange() {
      if (document.hidden) this.releaseAll();
    },

    /**
     * Record one input's state and emit only on the transition of the pair.
     *
     * @param {string} source 'pointer' or 'key'
     * @param {boolean} isDown
     */
    setSource(source, isDown) {
      const was = this.pointerHeld || this.keyHeld;
      if (source === "pointer") this.pointerHeld = isDown;
      else this.keyHeld = isDown;
      const now = this.pointerHeld || this.keyHeld;
      if (now === was) return;
      this.$emit(now ? "hold-start" : "hold-end");
    },

    releaseAll() {
      this.tapPointerId = null;
      this.tapKeyHeld = false;
      if (!this.pointerHeld && !this.keyHeld) return;
      this.pointerHeld = false;
      this.keyHeld = false;
      this.releasePointerCapture();
      this.$emit("hold-end");
    },

    releasePointerCapture() {
      const el = this.$refs.button;
      const id = this.capturedPointerId;
      this.capturedPointerId = null;
      if (!el || id == null) return;
      try {
        if (el.hasPointerCapture(id)) el.releasePointerCapture(id);
      } catch {
        /* the pointer is already gone; nothing to release */
      }
    },

    onPointerDown(event) {
      if (this.mode === "free") return;
      // Secondary buttons and the second finger of a two-finger touch are not
      // a press.
      if (event.isPrimary === false) return;
      if (event.button != null && event.button > 0) return;
      if (this.mode === "tap") {
        this.tapPointerId = event.pointerId;
        return;
      }
      // Capture so a finger that slides off the button still delivers its
      // pointerup here. Without it, dragging off and lifting leaves the gate
      // open with no event to close it.
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
        this.capturedPointerId = event.pointerId;
      } catch {
        /* capture is an improvement, not a requirement */
      }
      this.setSource("pointer", true);
    },

    // Bound to pointerup, pointercancel and lostpointercapture. The last two
    // are the ones that matter: a system gesture or a scroll steals the pointer
    // without ever sending pointerup.
    onPointerUp(event) {
      if (this.mode === "tap") {
        const tapped = this.tapPointerId != null &&
          this.tapPointerId === event.pointerId && event.type === "pointerup";
        this.tapPointerId = null;
        if (tapped) this.$emit("toggle-hold");
        return;
      }
      if (!this.pointerHeld) return;
      this.releasePointerCapture();
      this.setSource("pointer", false);
    },

    onClick(event) {
      // Hands-free: one tap is the whole interaction. A real <button> gives
      // Enter and Space here for free, which is why the document-level space
      // handling below stays out of this mode.
      if (this.mode === "free") this.$emit("toggle-free");
      // Keyboard/assistive activation has no pointerup; Space is prevented below.
      if (this.mode === "tap" && event.detail === 0) this.$emit("toggle-hold");
    },

    onKeyDown(event) {
      if (this.mode === "free") return;
      if (event.repeat) return;
      if (!isSpaceKey(event)) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      // Stops the page scrolling, and stops a focused button synthesising a
      // click out of the same key press.
      event.preventDefault();
      if (this.mode === "tap") {
        if (!this.tapKeyHeld) this.$emit("toggle-hold");
        this.tapKeyHeld = true;
        return;
      }
      this.setSource("key", true);
    },

    onKeyUp(event) {
      if (!isSpaceKey(event)) return;
      if (this.tapKeyHeld) {
        event.preventDefault();
        this.tapKeyHeld = false;
        return;
      }
      // Nothing to end: the press either started inside a text field or the
      // mode changed underneath it.
      if (!this.keyHeld) return;
      event.preventDefault();
      this.setSource("key", false);
    },
  },
};
</script>

<style scoped>
/* Plain CSS, no Tailwind: public/assets/styles.css is a committed, pre-compiled
   artefact that the Vue build does not regenerate, so a utility class not
   already in it silently does nothing. Same reasoning as VoiceControl.vue. */

.sa-voice-hold-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  font-family: Montserrat, sans-serif;
  text-align: center;
}

/* 88 px against a 72 px floor. This is pressed in a cold room, one-handed,
   often with a glove on, and the press is held rather than tapped — so it is
   sized for the pad of a thumb rather than for its tip. */
.sa-voice-hold {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 88px;
  height: 88px;
  min-width: 72px;
  min-height: 72px;
  padding: 0;

  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  background: #ffffff;
  color: #0c4a6e;
  cursor: pointer;

  /* The three lines that make a long press behave like a press: no scroll or
     pinch claimed from under the finger, no text selection, and no iOS callout
     menu over the top of the control being held. */
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;

  transition: background-color 120ms ease, color 120ms ease,
    box-shadow 120ms ease, transform 120ms ease;
}

.sa-voice-hold:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 3px;
}

/* Held. Filled rather than outlined, with a ring, because "am I being heard
   right now" has to be answerable from across a bench at a glance. */
.sa-voice-hold-down {
  background: #0c4a6e;
  color: #ffffff;
  box-shadow: 0 0 0 8px rgba(12, 74, 110, 0.18);
  transform: scale(1.04);
}

/* No session yet. Still pressable — the dock decides what a press means before
   one is running — just visibly not live. */
.sa-voice-hold-idle {
  border-color: #64748b;
  color: #64748b;
}

.sa-voice-hold-idle.sa-voice-hold-down {
  background: #64748b;
  border-color: #64748b;
  color: #ffffff;
  box-shadow: 0 0 0 8px rgba(100, 116, 139, 0.18);
}

.sa-voice-hold-label {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  color: #0c4a6e;
}

.sa-voice-hold-hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.2;
  color: #475569;
}

/* The space bar is a desktop affordance. On a touch screen the line is a lie,
   so it is not drawn there. */
@media (hover: none), (pointer: coarse) {
  .sa-voice-hold-hint {
    display: none;
  }
}

/* The sheet is half a phone screen; an 88 px button plus its label would take a
   third of that from the card. 64 px is still above the 44 px floor and the
   52 px gloved-hand preference. */
@media (max-width: 1023.98px) {
  .sa-voice-hold {
    width: 64px;
    height: 64px;
    min-width: 64px;
    min-height: 64px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sa-voice-hold {
    transition: none;
  }

  .sa-voice-hold-down {
    transform: none;
  }
}
</style>
